<template>
  <SettingsCard
    :title="$t('settings.company.language.title')"
    :subtitle="$t('settings.company.language.subtitle')"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="pa-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <div class="d-flex align-center">
              <span class="font-weight-bold">{{
                $t('settings.company.language.body-texts.0')
              }}</span>
            </div>
            <span class="text-grey">{{ $t('settings.company.language.body-texts.1') }}</span>
            <br />
            <span class="text-grey">
              {{ $t('settings.company.language.body-texts.2', { readablePlatformName }) }}
            </span>
            {{ ' ' }}
            <router-link
              to="/settings/profile?settings-open=language"
              class="text-decoration-underline text-primary"
            >
              {{ $t('settings.company.language.body-texts.3') }}
            </router-link>
            {{ ' ' }}
            <span class="text-grey">{{ $t('settings.company.language.body-texts.4') }}</span>
          </div>
        </v-col>

        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <v-select
            variant="outlined"
            :model-value="selectedLocale"
            :items="availableLocales"
            :disabled="showUpselling"
            @update:model-value="updateLanguage"
          >
            <template #item="{ props, item }">
              <v-list-item
                v-bind="props"
                :class="{ 'bg-primary': props.selected }"
                :title="$t(`language.${item.raw}`)"
              />
            </template>
            <template #selection="{ item }">
              {{ $t(`language.${item.value || item}`) }}
            </template>
          </v-select>
        </v-col>
      </v-row>
    </div>
    <BlurredBackdropUpselling v-if="showUpselling" />
  </SettingsCard>
</template>
<script>
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import { LanguageCodes } from '@/constants/languages'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import GET_AI_SETTINGS_LANGUAGE from '@/modules/settings/companySettings/queries/GetAiSettingsLanguage.gql'
import UPDATE_AI_SETTINGS_LANGUAGE from '@/modules/settings/companySettings/queries/UpdateAiSettingsLanguage.gql'
export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'language' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: { SettingsCard, BlurredBackdropUpselling },
  mixins: [brandingMixin, featureMixin],
  props: {
    showUpselling: {
      type: Boolean,
      default: false,
    },
    companyLocale: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      aiSettings: null,
      localeMapping: {
        en: 'en-GB',
        de: 'de-DE',
      },
    }
  },
  computed: {
    selectedLocale() {
      if (this.aiSettings?.language) {
        return this.aiSettings.language
      }
      if (this.companyLocale) {
        return this.companyLocale
      }
      const locale = this.$i18n.locale
      return this.localeMapping[locale] || locale
    },
    availableLocales() {
      const priorityLocales = ['de-DE', 'de-AT', 'de-CH', 'it-CH', 'fr-CH', 'en-GB', 'en-US']
      const getTranslatedLanguageName = (locale) => {
        const translationKey = `language.${locale}`
        return this.$t(translationKey, { locale: this.$i18n.locale }).toLowerCase()
      }

      const sortedLocales = [...LanguageCodes]
        .filter((locale) => !priorityLocales.includes(locale))
        .sort((a, b) => {
          const nameA = getTranslatedLanguageName(a)
          const nameB = getTranslatedLanguageName(b)
          return nameA.localeCompare(nameB)
        })

      if (this.aiSettings?.language) {
        return [...priorityLocales, ...sortedLocales]
      }

      return [...priorityLocales, ...sortedLocales].map(
        (locale) => this.localeMapping[locale] || locale
      )
    },
  },
  methods: {
    async updateLanguage(locale) {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_AI_SETTINGS_LANGUAGE,
          variables: {
            input: {
              language: locale,
            },
          },
          refetchQueries: [{ query: GET_AI_SETTINGS_LANGUAGE }],
        })
        this.$tracking.event('Settings', 'Edited', 'Update Content Creator Language', locale)
        showSnackbarMessage('success', this.$t('alerts.language-switch.success'))
      } catch (e) {
        showSnackbarMessage('error', this.$t('alerts.language-switch.error'))
      }
    },
  },
  apollo: {
    aiSettings: {
      query: GET_AI_SETTINGS_LANGUAGE,
    },
  },
}
</script>
